'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import {
  useGetCodeSetsVolumeQuery,
  useGetProviderVolumeQuery,
} from 'src/api/redux';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { applyDisplayMode, applyCaptureRate } from 'src/components/Profiles/shared/widgets/utils';

const CompactTreatingVolumePreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const codeSetIds = [
    ...(widget?.code_sets ?? []),
    ...(widget?.code_set ? [widget.code_set] : []),
  ].map((cs: { id: number }) => cs.id);
  const encounterId = widget?.encounter?.id;
  const entityId = entityDetails?.id;

  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    minYear != null || maxYear != null
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  const useEncounter = codeSetIds.length === 0 && !!encounterId;

  const { data: codeSetsData, isLoading: codeSetsLoading } =
    useGetCodeSetsVolumeQuery(
      {
        entityId: entityId || '',
        params: {
          code_set_ids: codeSetIds,
          all_code_sets: false,
          ...(minYear != null && { min_year: minYear }),
          ...(maxYear != null && { max_year: maxYear }),
        },
      },
      { skip: !entityId || useEncounter || codeSetIds.length === 0 }
    );

  const { data: encounterData, isLoading: encounterLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        cohort_id: encounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !useEncounter }
    );

  const isQueryLoading = useEncounter ? encounterLoading : codeSetsLoading;

  const data = useEncounter
    ? encounterData
      ? {
          total_claims: encounterData.claims,
          total_patients: encounterData.patients,
          total_providers: encounterData.providers,
        }
      : undefined
    : codeSetsData;

  const hasData = !!data;
  const displayMode = widget?.display_mode;
  const captureRate = widget?.capture_rate;
  const isMonthlyAvg = displayMode === 'monthly_average';
  const volumeType = widget?.volume_type;
  const showPatientsAsHero = volumeType === 'patients';
  const showProviders =
    hasData &&
    !isMonthlyAvg &&
    data.total_providers != null &&
    !isNaN(Number(data.total_providers));

  const formatValue = (value: number) =>
    handleNumericValue(applyCaptureRate(applyDisplayMode(value, displayMode), captureRate));

  const parentSummary = widgetConfig?.parentSummary;

  const summaryBlock = parentSummary ? (
    <p
      className="text-xs text-neutral-500 line-clamp-2"
      data-testid="compact-treating-volume-preview-summary"
    >
      {parentSummary}
    </p>
  ) : null;

  if (isQueryLoading || widgetConfig?.isLoading) {
    return (
      <div
        className="flex flex-col gap-2"
        data-testid="compact-treating-volume-preview-loading"
      >
        {summaryBlock}
        <WidgetLoading aria-label="Loading treating volume" />
      </div>
    );
  }

  if (!hasData) {
    return (
      <div
        className="flex flex-col gap-2"
        data-testid="compact-treating-volume-preview-empty"
      >
        {summaryBlock}
        <WidgetEmpty />
      </div>
    );
  }

  const heroRawValue = showPatientsAsHero
    ? Number(data.total_patients)
    : Number(data.total_claims);
  const heroValue = formatValue(heroRawValue);
  const heroLabel = showPatientsAsHero ? 'Patients' : 'Claims';

  const hasSubtitle = !volumeType;
  const subtitleRawValue = showPatientsAsHero
    ? Number(data.total_claims)
    : Number(data.total_patients);
  const subtitleValue = formatValue(subtitleRawValue);
  const subtitleLabel = showPatientsAsHero ? 'Claims' : 'Patients';

  const dateRangeLabel = widgetConfig?.dateRangeLabel;

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="compact-treating-volume-preview"
    >
      {showProviders && (
        <p
          className="flex items-center gap-1.5 text-xs text-neutral-500"
          data-testid="compact-treating-volume-preview-providers"
        >
          <span>{formatValue(Number(data.total_providers))} Providers</span>
          {dateRangeLabel && (
            <>
              <Icon
                icon={faCircle}
                className="text-neutral-300 !h-[0.375rem] !w-[0.375rem] flex-shrink-0"
                aria-hidden="true"
              />
              <span>{dateRangeLabel}</span>
            </>
          )}
        </p>
      )}
      {summaryBlock}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline gap-3">
          <span
            className="text-4xl font-bold tabular-nums text-sky-600"
            data-testid="compact-treating-volume-preview-hero-value"
          >
            {heroValue}
          </span>
          <span
            className="text-sm font-semibold text-neutral-500"
            data-testid="compact-treating-volume-preview-hero-label"
          >
            {heroLabel}
          </span>
        </div>
        {hasSubtitle && (
          <p
            className="text-sm text-neutral-500"
            data-testid="compact-treating-volume-preview-subtitle"
          >
            {subtitleValue} {subtitleLabel}
          </p>
        )}
      </div>
    </div>
  );
};

export default CompactTreatingVolumePreview;
